import { Play, Pause, ShoppingCart, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { getPricing } from "../lib/pricing";
import { motion } from "framer-motion";

export default function BeatCard({ beat, allBeats, onBuy }) {
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();
  const isCurrentTrack = currentTrack?.id === beat.id;
  const isThisPlaying = isCurrentTrack && isPlaying;
  const { isFree, hasDiscount, original, final, discount } = getPricing(beat);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="beat-card group bg-card rounded-2xl border border-border p-4 hover:border-primary/50 transition-all"
      data-testid={`beat-card-${beat.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Cover Image with Play Button */}
        <div className="relative flex-shrink-0">
          {(isFree || hasDiscount) && (
            <div className="absolute -top-2 -left-2 z-10">
              <Badge className={`text-[10px] font-bold px-1.5 py-0.5 ${isFree ? "bg-green-500 text-white" : "bg-primary text-white"}`}>
                {isFree ? "FREE" : `-${discount}%`}
              </Badge>
            </div>
          )}
          <img
            src={beat.cover_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${beat.cover_url}` : beat.cover_url}
            alt={beat.title}
            className="w-16 h-16 lg:w-20 lg:h-20 rounded-xl object-cover"
          />
          <Button
            onClick={() => playTrack(beat, allBeats)}
            size="icon"
            className={`absolute inset-0 m-auto w-10 h-10 rounded-full transition-all ${
              isThisPlaying
                ? "bg-primary opacity-100"
                : "bg-black/60 opacity-0 group-hover:opacity-100"
            }`}
            data-testid={`play-btn-${beat.id}`}
          >
            {isThisPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </Button>
          
          {/* Playing indicator */}
          {isThisPlaying && (
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-0.5 h-2 bg-primary rounded-full audio-bar"
                />
              ))}
            </div>
          )}
        </div>

        {/* Beat Info */}
        <div className="flex-1 min-w-0">
          <Link to={`/beat/${beat.id}`} data-testid={`beat-detail-link-${beat.id}`}>
            <h3 className="font-heading font-bold text-base lg:text-lg truncate hover:text-primary transition-colors">
              {beat.title}
            </h3>
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs font-mono">
              {beat.bpm} BPM
            </Badge>
            <Badge variant="secondary" className="text-xs font-mono">
              {beat.key}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(beat.genres?.length ? beat.genres : [beat.genre]).map((g) => (
              <Badge key={g} variant="outline" className="text-xs">
                {g}
              </Badge>
            ))}
          </div>
        </div>

        {/* Price and Buy */}
        <div className="flex flex-col items-end gap-2">
          {isFree ? (
            <Badge className="bg-green-500/20 text-green-400 font-bold" data-testid={`free-badge-${beat.id}`}>FREE</Badge>
          ) : hasDiscount ? (
            <div className="flex flex-col items-end leading-tight">
              <span className="text-xs text-muted-foreground line-through">${original}</span>
              <span className="font-heading font-bold text-lg text-primary">${final}</span>
            </div>
          ) : (
            <span className="font-heading font-bold text-lg text-primary">${original}</span>
          )}
          <Button
            onClick={() => onBuy(beat)}
            size="sm"
            className="rounded-full bg-primary hover:bg-primary/90 neon-glow gap-2"
            data-testid={`buy-btn-${beat.id}`}
          >
            {isFree ? <Download className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
            <span className="hidden sm:inline">{isFree ? "Free" : "Buy"}</span>
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
